import { useSetAddressError } from 'feature/addresses/hooks/useSetAddressError'
import { useAddressSliceActions } from 'feature/addresses/hooks/useAddressSliceActions'
import {
  useAddressState,
  AddressSliceKey,
} from 'feature/addresses/hooks/useAddressState'
import { yandexMap } from 'feature/addresses/redux/addressCreateSelector'
import { SavedAddressList } from 'feature/addresses/redux/MyAddresses.api'
import { AddressErrorsList } from 'feature/addresses/types'
import { getSuggestsQuery } from 'feature/DaData/getSuggestsQuery'
import { TSuggest } from 'feature/registration/types'
import { ScreenEnum, useScreen } from 'feature/screenMode'
import { isAddressWithError } from 'helpers/addressErrorBoundary'
import { debounce } from 'helpers/debounce'
import {
  ChangeEvent,
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useRef,
} from 'react'
import { useActions, useAppSelector } from 'store/hooks'

import { TDadataSelectInput } from '../DadataSelectInput'
import axios from 'axios'

type TUseHandlers = {
  props: TDadataSelectInput
  setUserAddressVisible: Dispatch<SetStateAction<boolean>>
  setDadataSelectVisible: Dispatch<SetStateAction<boolean>>
  addressesData: SavedAddressList | undefined
  isLoading: boolean
  isDadataSelectVisible: boolean
  selectInputRefInMui: MutableRefObject<HTMLInputElement | null>
  addError: () => void
  addressKey?: AddressSliceKey
}

export const useHandlers = ({
  props,
  setUserAddressVisible,
  setDadataSelectVisible,
  addressesData,
  isLoading,
  isDadataSelectVisible,
  selectInputRefInMui,
  addError,
  addressKey = 'default',
}: TUseHandlers) => {
  const { onSelect, inputProps, onChange, onClickAway, clearErrorsAddress } =
    props

  const abortControllerRef = useRef<AbortController | null>(null)

  const {
    setInputAddress,
    setInputAddressSuggest,
    setIsValidAddress,
    setIsDirtyAddress,
    setSuggests,
    setIsLoadingSuggest,
  } = useAddressSliceActions(addressKey)

  const { clearMap } = useActions()

  const screen = useScreen()

  const { clickMark } = useAppSelector(yandexMap)
  const { input, isDirty: isDirtyAddress } = useAddressState(addressKey)
  const setAddressError = useSetAddressError(addressKey)

  const shortInputTimerRef = useRef<ReturnType<typeof setTimeout>>()

  // isSelect: подсказка выбрана из списка руками — там уже есть точное
  // совпадение адреса, полную проверку (fiasLevel и т.п.) делать не нужно
  const getSuggests = async (
    query: string,
    options?: { isSelect?: boolean },
  ) => {
    abortControllerRef.current?.abort()
    abortControllerRef.current = new AbortController()

    setIsLoadingSuggest(true)
    getSuggestsQuery(query, abortControllerRef.current.signal)
      .then(({ data }) => {
        if (query.length < 4) {
          setSuggests([])
          setIsValidAddress(false)
          return
        }
        setSuggests(data)

        if (options?.isSelect) {
          const suggestValue = data[0]?.address.replace(/\n/g, '')
          setIsValidAddress(
            query === suggestValue || query === suggestValue + ',',
          )
          return
        }

        // проверяем сразу по свежему ответу дадаты (data[0]), а не по
        // inputSuggest из стора — тот обновляется только при явном выборе
        // подсказки и на момент набора текста является устаревшим
        const hasAddressError = isAddressWithError({
          inputAddress: query,
          inputSuggest: data[0] ?? null,
          setAddressError,
        })

        if (hasAddressError) {
          setIsValidAddress(false)
        } else {
          clearErrorsAddress()
          setIsValidAddress(true)
        }
      })
      .catch(err => {
        if (axios.isCancel(err)) return

        if (err.response && err.response.status === 500) {
          addError()
        }
      })
      .finally(() => setIsLoadingSuggest(false))
  }

  const debounceGetSuggests = debounce(getSuggests, 300)

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\n/g, '')

    clearTimeout(shortInputTimerRef.current)
    setIsDirtyAddress(true)
    setInputAddress(value)

    if (!value.length) {
      setInputAddress('')
      clearErrorsAddress()
      if (addressKey !== 'company') {
        clearMap()
      }
      return
    }

    if (onChange) {
      onChange(e)
    }

    if (inputProps.error) {
      clearErrorsAddress()
    }

    if (value.length < 4) {
      setIsValidAddress(false)
      // короткую строку не проверяем на каждый символ, а только когда
      // пользователь сделал паузу в наборе
      shortInputTimerRef.current = setTimeout(() => {
        isAddressWithError({
          inputAddress: value,
          inputSuggest: null,
          setAddressError,
        })
      }, 300)
      return
    }
    if (value.length > 300) {
      setInputAddress(value.slice(0, 300))
      setAddressError(AddressErrorsList.addressIsLong)
      setIsValidAddress(false)
      return
    }

    if (!isDadataSelectVisible) {
      setDadataSelectVisible(true)
    }
    if (addressesData) {
      setUserAddressVisible(false)
    }

    debounceGetSuggests(value)
  }

  const selectHandler = (item: TSuggest) => {
    setIsDirtyAddress(true)
    if (screen !== ScreenEnum.desktop) {
      clearErrorsAddress()
    }
    selectInputRefInMui.current?.focus()

    const searchValue = item.house === null ? item.address + ',' : item.address

    debounceGetSuggests(searchValue, { isSelect: true }).then(() => {
      setInputAddressSuggest(item)
      setInputAddress(item.address)

      if (onSelect) {
        onSelect(item)
      }
      setIsValidAddress(true)
      setDadataSelectVisible(false)
    })
  }

  const onClickAwayHandler = (e: MouseEvent | TouchEvent) => {
    setDadataSelectVisible(false)

    if (input.length === 0 && inputProps.required) {
      setAddressError(AddressErrorsList.addressIsRequired)
      setIsValidAddress(false)
    }

    if (addressesData) {
      setUserAddressVisible(false)
    }

    if (!isDirtyAddress && !clickMark.coords) return
    if (onClickAway) {
      onClickAway(e)
    }
  }

  const handleClick = () => {
    if ((addressesData?.list?.length || isLoading) && !input?.length) {
      setUserAddressVisible(true)

      return
    }
    setDadataSelectVisible(true)

    if (input.length !== 0 && inputProps.required) {
      return
    }

    clearErrorsAddress()
  }

  return { handleChange, selectHandler, onClickAwayHandler, handleClick }
}
